import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Shared modal for the 6-stage order workflow transitions.
 * Used by ProBoundOrderDetail, ProEpoxyOrderDetail, ProDecorOrderDetail.
 *
 * Props:
 *   action         One of 'invoiced' | 'paid' | 'payment_confirmed' | 'dispatched' | 'delivered' | null
 *   order          The order doc (for reference / invoice_number display)
 *   invoiceNumber, setInvoiceNumber
 *   paymentMethod, setPaymentMethod
 *   expectedEta, setExpectedEta
 *   submitting     boolean
 *   onClose        () => void
 *   onConfirm      () => void                — caller decides which fields to send
 *   accentClass    Tailwind class for the confirm button background (per product)
 */
export const PAYMENT_METHODS = [
  { key: 'go_cardless',   label: 'GoCardless' },
  { key: 'bank_transfer', label: 'Bank Transfer' },
  { key: 'card',          label: 'Card' },
  { key: 'cash',          label: 'Cash' },
];

export default function OrderStageActionModal({
  action,
  order,
  invoiceNumber, setInvoiceNumber,
  paymentMethod, setPaymentMethod,
  expectedEta, setExpectedEta,
  submitting,
  onClose,
  onConfirm,
  accentClass = 'bg-[#2F5E96] hover:bg-[#1f4068]',
  isStaff = true,
}) {
  // ProBound snapshots carry an inc-VAT total; ProDecor/ProEpoxy/material `total`/`subtotal` are excl. VAT → gross up for the invoice figure.
  const exVat = order?.calculation?.totalCost ?? order?.total ?? ((Number(order?.subtotal) || 0) - (Number(order?.credit_applied) || 0));
  const appTotal = order?.pricing_snapshot?.total_inc_vat ?? Math.round((Number(exVat) || 0) * 1.2 * 100) / 100;
  const estimateHint = order?.pricing_snapshot?.total_inc_vat != null ? '' : ` (app shows ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(exVat) || 0)} excl. VAT + 20%)`;
  const [confirmedTotal, setConfirmedTotal] = useState('');
  const [payTick, setPayTick] = useState(false);
  const [payReference, setPayReference] = useState('');
  useEffect(() => {
    if (action === 'invoiced') { const v = order?.confirmed_total ?? order?.xero_invoice_total ?? appTotal; setConfirmedTotal(v ? Number(v).toFixed(2) : ''); }
    if (action === 'paid') { setPayTick(false); setPayReference(''); if (!paymentMethod) setPaymentMethod?.('bank_transfer'); }
  }, [action]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!action) return null;
  const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n) || 0);
  const installerPaying = action === 'paid' && !isStaff;
  const confirmedMissing = installerPaying && order?.confirmed_total == null;
  const extra = action === 'invoiced' ? { confirmed_total: parseFloat(confirmedTotal) }
    : installerPaying ? { payment_method: paymentMethod, payment_reference: payReference.trim(), paid_amount_confirmed: true } : {};

  const titleMap = {
    invoiced:          'Mark as invoiced',
    paid:              'Mark invoice as paid',
    payment_confirmed: 'Mark as payment confirmed',
    dispatched:        'Mark as dispatched',
    delivered:         'Mark as delivered',
  };

  const confirmDisabled =
    submitting
    || (action === 'invoiced' && (!invoiceNumber.trim() || !(parseFloat(confirmedTotal) > 0)))
    || (action === 'payment_confirmed' && !paymentMethod)
    || (installerPaying && (confirmedMissing || !payTick || !paymentMethod));

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stage-action-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">{titleMap[action]}</h3>
        </div>
        <div className="p-4 space-y-3">
          {action === 'invoiced' && (
            <>
              <p className="text-sm text-gray-700">
                Enter the Xero invoice number for <strong>{order.reference}</strong>.
                The installer will be emailed asking them to mark the invoice as paid once they&apos;ve settled it.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500">Invoice number</label>
                <input
                  data-testid="stage-invoice-number"
                  autoFocus
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="e.g. INV-2026-0123"
                  className="mt-1 w-full border-2 rounded-lg p-2.5 text-sm"
                />
              </div>
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3">
                <label className="text-xs uppercase tracking-wider font-bold text-amber-900">Confirmed invoice total (inc. VAT)</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                  <input data-testid="stage-confirmed-total" type="number" step="0.01" min="0" value={confirmedTotal} onChange={(e) => setConfirmedTotal(e.target.value)} className="w-full border-2 rounded-lg p-2.5 pl-7 text-lg font-bold" />
                </div>
                <p className="text-xs text-amber-900 mt-1.5">App estimate <strong>{fmt(appTotal)}</strong> inc. VAT{estimateHint}{order?.xero_invoice_total != null && <> · Xero invoice <strong>{fmt(order.xero_invoice_total)}</strong></>}. Check against Xero — this is the exact amount the installer is told to pay, and unlocks their Mark as Paid.</p>
              </div>
            </>
          )}
          {action === 'paid' && !installerPaying && (
            <p className="text-sm text-gray-700">
              Mark invoice <strong>{order.invoice_number || '(no number on file)'}</strong> for order <strong>{order.reference}</strong> as paid
              {order.confirmed_total != null && <> (confirmed total <strong>{fmt(order.confirmed_total)}</strong>)</>}.
            </p>
          )}
          {installerPaying && confirmedMissing && (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="stage-pay-not-confirmed">
              <strong>Hold on — Ox Resin haven't confirmed the amount yet.</strong> The total shown is the app's estimate. Please wait for the confirmed total (you'll get an email) before paying.
            </p>
          )}
          {installerPaying && !confirmedMissing && (
            <>
              <p className="text-sm text-gray-700">Ox Resin confirmed the amount for <strong>{order.reference}</strong>{order.invoice_number ? <> (invoice <strong>{order.invoice_number}</strong>)</> : null} as</p>
              <p className="text-2xl font-black text-emerald-700 inline-flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> {fmt(order.confirmed_total)}</p>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500">How did you pay?</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((m) => (
                    <button key={m.key} type="button" data-testid={`stage-pay-method-${m.key}`} onClick={() => setPaymentMethod(m.key)} className={`px-3 py-2 rounded border text-sm font-semibold transition-colors ${paymentMethod === m.key ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-slate-400'}`}>{m.label}</button>
                  ))}
                </div>
              </div>
              <input data-testid="stage-pay-reference" type="text" value={payReference} onChange={(e) => setPayReference(e.target.value)} placeholder="Payment reference (optional)" maxLength={80} className="w-full border-2 rounded-lg p-2.5 text-sm" />
              <label className="flex items-start gap-2 text-sm text-gray-800 rounded-lg border border-gray-200 p-3 cursor-pointer">
                <input type="checkbox" checked={payTick} onChange={(e) => setPayTick(e.target.checked)} className="mt-0.5" data-testid="stage-pay-tick" />
                <span>I confirm I have paid <strong>{fmt(order.confirmed_total)}</strong> — the amount confirmed by the Ox Resin office — for this order.</span>
              </label>
            </>
          )}
          {action === 'payment_confirmed' && (
            <>
              <p className="text-sm text-gray-700">
                Confirm that payment for <strong>{order.reference}</strong> has landed in the Ox Resin account.
                The installer will be emailed a thank-you and told the order is being processed.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500">Payment method</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      data-testid={`stage-payment-method-${m.key}`}
                      onClick={() => setPaymentMethod(m.key)}
                      className={`px-3 py-2 rounded border text-sm font-semibold transition-colors ${
                        paymentMethod === m.key
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {action === 'dispatched' && (
            <>
              <p className="text-sm text-gray-700">
                Confirm dispatch for <strong>{order.reference}</strong>. The installer will be emailed that their order is on its way.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500">Expected delivery date (optional)</label>
                <input
                  data-testid="stage-eta-date"
                  type="date"
                  value={expectedEta}
                  onChange={(e) => setExpectedEta(e.target.value)}
                  className="mt-1 w-full border-2 rounded-lg p-2.5 text-sm"
                />
              </div>
            </>
          )}
          {action === 'delivered' && (
            <p className="text-sm text-gray-700">
              Confirm you&apos;ve received the materials for <strong>{order.reference}</strong>.
              This closes the order — if anything is missing or damaged, reply to the dispatch email or contact Ox Resin first.
            </p>
          )}
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting} data-testid="stage-action-cancel">Cancel</Button>
          <Button
            size="sm"
            disabled={confirmDisabled}
            onClick={() => onConfirm(extra)}
            className={`${accentClass} text-white`}
            data-testid="stage-action-confirm"
          >
            {submitting ? 'Updating…' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
}
